import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import AdminLayout from './AdminLayout';

const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  navy: '#0b1120',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  creamPale: '#f8f5ef',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

const CATEGORIES = [
  'Technical Papers',
  'Case Studies',
  'Industry Reports',
  'Training Materials',
  'Project Reports',
  'Research & Development',
  'Presentations',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => currentYear - i);

export default function PublicationUploader() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [year, setYear] = useState(currentYear);
  const [tags, setTags] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    const { data } = await supabase
      .from('publications')
      .select('*')
      .order('year', { ascending: false });
    if (data) setPublications(data);
    setLoading(false);
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setCategory(''); setYear(currentYear);
    setTags(''); setPdfFile(null); setCoverFile(null); setShowForm(false);
  };

  const handleUpload = async () => {
    if (!title || !pdfFile) {
      alert('Title and PDF file are required');
      return;
    }

    if (pdfFile.type !== 'application/pdf') {
      alert('Only PDF files are allowed');
      return;
    }

    if (pdfFile.size > 50 * 1024 * 1024) {
      alert('PDF must be under 50MB');
      return;
    }

    setUploading(true);

    const safeName = pdfFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const pdfPath = `${Date.now()}_${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from('publications')
      .upload(pdfPath, pdfFile, { contentType: 'application/pdf' });

    if (uploadError) {
      alert('PDF upload failed: ' + uploadError.message);
      setUploading(false);
      return;
    }

    supabase.storage.from('publications').getPublicUrl(pdfPath);

    let coverUrl = '';
    if (coverFile) {
      const coverName = coverFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const coverPath = `covers/${Date.now()}_${coverName}`;
      const { error: coverError } = await supabase.storage
        .from('article-images')
        .upload(coverPath, coverFile, { contentType: coverFile.type });
      if (!coverError) {
        const { data: coverUrlData } = supabase.storage.from('article-images').getPublicUrl(coverPath);
        coverUrl = coverUrlData.publicUrl;
      }
    }

    const { error: dbError } = await supabase.from('publications').insert({
      title: title.trim(),
      description: description.trim(),
      file_path: pdfPath,
      file_name: pdfFile.name,
      file_size: pdfFile.size,
      cover_image: coverUrl,
      category,
      year: parseInt(year),
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      status: 'published',
    });

    if (dbError) {
      alert('Save failed: ' + dbError.message);
    } else {
      resetForm();
      fetchPublications();
    }

    setUploading(false);
  };

  const deletePublication = async (pub) => {
    if (!window.confirm(`Delete "${pub.title}"?`)) return;
    await supabase.storage.from('publications').remove([pub.file_path]);
    await supabase.from('publications').delete().eq('id', pub.id);
    setPublications(publications.filter(p => p.id !== pub.id));
  };

  const toggleStatus = async (pub) => {
    const newStatus = pub.status === 'published' ? 'draft' : 'published';
    await supabase.from('publications').update({ status: newStatus }).eq('id', pub.id);
    setPublications(publications.map(p => p.id === pub.id ? { ...p, status: newStatus } : p));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', border: `1px solid ${C.creamWarm}`,
    background: C.creamPale, fontFamily: "'Manrope', sans-serif", fontSize: 14,
    color: C.textDark, outline: 'none',
  };

  const labelStyle = {
    display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 2,
    textTransform: 'uppercase', color: C.textBody, marginBottom: 8,
  };

  return (
    <AdminLayout title="Publications">
      {/* Top Bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <div style={{ fontSize: 14, color: C.textMuted }}>
          {publications.length} publication{publications.length !== 1 ? 's' : ''}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '12px 28px', background: C.blue, color: C.navy, border: 'none',
            fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ Upload Publication'}
        </button>
      </div>

      {/* Upload Form */}
      {showForm && (
        <div style={{
          background: C.white, border: `1px solid ${C.creamWarm}`, padding: 32,
          marginBottom: 24,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Title *</label>
              <input style={inputStyle} placeholder="Publication title" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, resize: 'none', height: 80 }} placeholder="Brief description of this publication..." value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Year</label>
              <select style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }} value={year} onChange={e => setYear(e.target.value)}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tags (comma separated)</label>
              <input style={inputStyle} placeholder="steel, ironmaking, energy" value={tags} onChange={e => setTags(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Cover Image (optional)</label>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setCoverFile(e.target.files[0])}
                style={{ ...inputStyle, padding: '10px 16px', cursor: 'pointer' }} />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>PDF File *</label>
            <input type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files[0])}
              style={{ ...inputStyle, padding: '10px 16px', cursor: 'pointer' }} />
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              padding: '14px 32px', background: C.blue, color: C.navy, border: 'none',
              fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
              letterSpacing: 2, textTransform: 'uppercase', cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.7 : 1,
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Publication'}
          </button>
        </div>
      )}

      {/* Publications List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading...</div>
      ) : publications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>
          No publications yet. Click "Upload Publication" to add your first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {publications.map(pub => (
            <div key={pub.id} style={{
              background: C.white, border: `1px solid ${C.creamWarm}`, padding: '16px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <span style={{
                    padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    textTransform: 'uppercase', background: 'rgba(102,153,255,0.08)', color: C.blue,
                  }}>PDF</span>
                  <span style={{
                    padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    textTransform: 'uppercase',
                    background: pub.status === 'published' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
                    color: pub.status === 'published' ? '#16a34a' : '#ca8a04',
                  }}>{pub.status}</span>
                  {pub.category && <span style={{ fontSize: 11, color: C.textMuted }}>{pub.category}</span>}
                  <span style={{ fontSize: 11, color: C.textMuted }}>{pub.year}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.textDark }}>{pub.title}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  {pub.file_name} · {formatSize(pub.file_size)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => toggleStatus(pub)} style={{
                  padding: '6px 14px', border: '1px solid rgba(58,72,96,0.15)', background: 'rgba(58,72,96,0.05)',
                  color: C.textBody, fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', borderRadius: 2,
                }}>
                  {pub.status === 'published' ? 'Unpublish' : 'Publish'}
                </button>
                <button onClick={() => deletePublication(pub)} style={{
                  padding: '6px 14px', border: '1px solid rgba(220,38,38,0.15)', background: 'rgba(220,38,38,0.05)',
                  color: '#dc2626', fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', borderRadius: 2,
                }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}